import React, { useMemo } from 'react';
import { DataTable, Column } from '@/components/ui/data-table';
import { AudiencePill, AtsSourceIcon, StatusBadge, getStatusAccentColor } from '@/components/ui/table-components';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Pencil, Trash2 } from 'lucide-react';
import { Job } from '../../interfaces';

const statusLabels: Record<string, string> = {
  PLATFORM_INCLUDED: 'Pending Opt-in',
  SHARED: 'Shared',
  STOPPED_SHARING: 'Draft',
  REMOVED: 'Removed',
  UNKNOWN: 'Unknown',
};

interface RolesTableProps {
  roles: Job[];
  locationName: (location: string) => string[];
  onRoleClick: (role: Job) => void;
  onEditRole: (role: Job) => void;
  onDeleteRole: (roleId: number) => void;
  sortBy?: string;
  sortDirection?: 'asc' | 'desc';
  onSort?: (columnId: string) => void;
  isLoading?: boolean;
}

export function RolesTable({
  roles,
  locationName,
  onRoleClick,
  onEditRole,
  onDeleteRole,
  sortBy,
  sortDirection,
  onSort,
  isLoading = false,
}: RolesTableProps) {
  const columns: Column<Job>[] = useMemo(
    () => [
      {
        id: 'clientName',
        header: 'Client',
        sortable: true,
        cell: (role) => (
          <div>
            <div className="font-medium text-slate-900 dark:text-slate-100">
              {role.clientName.replace('[VMS] ', '')}
            </div>
            {role.externalId && (
              <div className="mt-1">
                <AtsSourceIcon externalId={role.externalId} />
              </div>
            )}
          </div>
        ),
      },
      {
        id: 'title',
        header: 'Job Title',
        sortable: true,
        cell: (role) => (
          <div className="font-medium text-slate-900 dark:text-slate-100">
            {role.title}
          </div>
        ),
      },
      {
        id: 'industry',
        header: 'Industry',
        sortable: true,
        cell: (role) => (
          <span className="text-slate-700 dark:text-slate-300">
            {role.industry || '-'}
          </span>
        ),
      },
      {
        id: 'location.displayName',
        header: 'Location',
        sortable: true,
        cell: (role) => (
          <span className="text-slate-700 dark:text-slate-300">
            {locationName(role?.location?.displayName)}
          </span>
        ),
      },
      {
        id: 'workType',
        header: 'Type',
        sortable: true,
        className: 'text-center',
        cell: (role) => (
          <Badge variant="secondary" className="font-mono text-xs">
            {role.workType
              ? role.workType === 'TEMP'
                ? 'CONTRACT/TEMP'
                : 'PERMANENT'
              : '-'}
          </Badge>
        ),
      },
      {
        id: 'audience',
        header: 'Audience',
        sortable: true,
        className: 'text-center',
        cell: (role) => <AudiencePill audience={role.audience} />,
      },
      {
        id: 'matches',
        header: 'Matches',
        sortable: true,
        className: 'text-center',
        cell: (role) => (
          <Badge
            variant={role.matches ? 'default' : 'secondary'}
            className="font-serif text-base font-semibold"
          >
            {role.matches || 0}
          </Badge>
        ),
      },
      {
        id: 'status',
        header: 'Status',
        sortable: true,
        className: 'text-center',
        cell: (role) => (
          <StatusBadge status={statusLabels[role.status] || 'Unknown'} />
        ),
      },
      {
        id: 'actions',
        header: 'Actions',
        width: '120px',
        className: 'text-center',
        cell: (role) => (
          <div className="flex items-center justify-center gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                onEditRole(role);
              }}
              className="hover:bg-teal-50 hover:text-teal-700 dark:hover:bg-teal-900/30 dark:hover:text-teal-300"
            >
              <Pencil className="h-4 w-4" />
            </Button>
            <Button
              size="sm"
              variant="ghost"
              onClick={(e) => {
                e.stopPropagation();
                onDeleteRole(role.id);
              }}
              className="hover:bg-rose-50 hover:text-rose-700 dark:hover:bg-rose-900/30 dark:hover:text-rose-300"
            >
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ),
      },
    ],
    [locationName, onEditRole, onDeleteRole]
  );

  const getRowId = (role: Job, index: number) => {
    if (role?.id != null && role.id !== 0) {
      return `job-${role.id}`;
    }
    if (role?.externalId) {
      return `job-ext-${role.externalId}`;
    }
    return `job-idx-${index}`;
  };

  return (
    <DataTable
      data={roles}
      columns={columns}
      onRowClick={onRoleClick}
      getRowId={getRowId}
      getRowAccentColor={(role) => getStatusAccentColor(role.status)}
      sortBy={sortBy}
      sortDirection={sortDirection}
      onSort={onSort}
      isLoading={isLoading}
      emptyMessage="You have no roles included at this time. Keep an eye on your inbox as we will send you an email when we detect potential candidates for your roles."
    />
  );
}
